import React, { useState, useEffect, useCallback } from 'react';
import { Joyride, STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';
import { useAuth, useLang } from '../App';

const TOUR_STORAGE_KEY = 'idfc_tour_seen_session';
export const RELAUNCH_TOUR_EVENT = 'idfc-relaunch-tour';

const getTourSteps = (role, tourLang) => {
  const fr = tourLang === 'fr';
  const s = (target, tFr, tEn, cFr, cEn, placement = 'right') => ({
    target, title: fr ? tFr : tEn, content: fr ? cFr : cEn, placement, disableBeacon: true,
  });

  const steps = [
    // 1. Intro (centered)
    {
      target: 'body',
      placement: 'center',
      disableBeacon: true,
      title: fr ? 'Bienvenue sur GCFE' : 'Welcome to GCFE',
      content: fr
        ? 'Cette plateforme connecte les banques de développement membres de l\'IDFC autour de 8 piliers : pipeline de projets partagé, matching intelligent de partenaires, intelligence projet, collaboration sécurisée, alertes d\'opportunités, knowledge hub, analytics de portefeuille, et gouvernance & sécurité de niveau bancaire. Ce tour vous les présente en 2 minutes.'
        : 'This platform connects the IDFC member development banks around 8 pillars: shared project pipeline, smart partner matching, project intelligence, secure collaboration, opportunity alerts, knowledge hub, portfolio analytics, and banking-grade governance & security. This tour walks you through them in 2 minutes.',
    },

    // PILIER 1 — Shared Project Pipeline
    s('[data-testid="nav-submit-project"]',
      '1. Pipeline de Projets Partagé', '1. Shared Project Pipeline',
      'Soumettez un projet (formulaire guidé + analyse IA + OCR des documents scannés) : il apparaît instantanément dans l\'espace partagé des membres, selon la visibilité que vous choisissez (public, restreint ou confidentiel avec sélection des institutions autorisées). Le Dashboard liste tous les projets du réseau avec leurs indicateurs clés.',
      'Submit a project (guided form + AI analysis + OCR for scanned documents): it instantly appears in the shared space of the members, according to the visibility you choose (public, restricted, or confidential with selected allowed institutions). The Dashboard lists every network project with its key indicators.'
    ),

    // PILIER 2 — Smart Partner Matching
    s('[data-testid="nav-institutions"]',
      '2. Matching Intelligent de Partenaires', '2. Smart Partner Matching',
      'Pour chaque projet, un score de co-financement (0-100) est calculé pour chacune des institutions membres selon des critères objectifs : secteurs prioritaires, zones géographiques, ticket min/max, appétit au risque. Vos critères d\'investissement (saisis lors de votre première connexion) personnalisent vos recommandations. Le bouton "Pourquoi ?" génère une justification IA, rédigée dans votre langue, avec points de discussion concrets. Résultats cohérents, jamais vides ni aléatoires.',
      'For each project, a co-financing score (0-100) is computed for each member institution using objective criteria: priority sectors, geographies, min/max ticket, risk appetite. Your investment criteria (captured at your first login) personalize your recommendations. The "Why?" button generates an AI rationale, written in your language, with concrete talking points. Results are consistent, never empty nor random.'
    ),

    // PILIER 3 — Project Intelligence
    s('[data-testid="nav-impact"]',
      '3. Intelligence Projet & Impact ODD/Climat', '3. Project Intelligence & SDG/Climate Impact',
      'Chaque fiche projet est standardisée : score de bankabilité, note de maturité (A à D), checklist de due diligence, documents analysés par IA. Un radar ODD compare l\'impact du projet aux moyennes du réseau. Le propriétaire peut DÉSACTIVER l\'IA sur un projet sensible (souveraineté des données). Le Tableau de Bord d\'Impact agrège l\'alignement ODD et Accord de Paris de tout le réseau : CO2 évité, personnes desservies, emplois créés, part de finance climat.',
      'Every project sheet is standardized: bankability score, readiness grade (A to D), due diligence checklist, AI-analyzed documents. An SDG radar compares the project\'s impact against network averages. The owner can DISABLE AI on a sensitive project (data sovereignty). The Impact Dashboard aggregates SDG and Paris Agreement alignment across the network: CO2 avoided, people served, jobs created, climate finance share.'
    ),

    // PILIER 4 — Secure Collaboration
    s('[data-testid="nav-pipeline"]',
      '4. Collaboration Sécurisée (Deal Rooms)', '4. Secure Collaboration (Deal Rooms)',
      'Chaque projet ouvre une deal room isolée : partage de documents (upload sécurisé + téléchargement audité), messagerie entre institutions, notes internes visibles uniquement par votre institution, visioconférence Jitsi intégrée. Le pipeline Kanban 5 colonnes (glisser-déposer) suit chaque deal de la prospection à la clôture.',
      'Each project opens an isolated deal room: document sharing (secure upload + audited downloads), inter-institution messaging, internal notes visible only to your institution, integrated Jitsi video calls. The 5-column Kanban pipeline (drag & drop) tracks each deal from prospecting to closing.'
    ),

    // PILIER 5 — Opportunity Alerts
    s('[data-testid="notifications-button"]',
      '5. Alertes d\'Opportunités', '5. Opportunity Alerts',
      'Notifications en temps réel (WebSocket) : nouveau projet correspondant à vos critères, activité dans un deal room, changement d\'étape, invitation visio. Configurez vos ALERTES INTELLIGENTES (secteur, pays, montant) depuis votre Profil : dès qu\'un projet matche, vous êtes prévenu instantanément.',
      'Real-time notifications (WebSocket): new project matching your criteria, deal room activity, stage change, video call invite. Configure your SMART ALERTS (sector, country, amount) from your Profile: the moment a project matches, you are instantly notified.',
      'bottom'
    ),

    // PILIER 6 — Knowledge Hub
    s('[data-testid="nav-knowledge"]',
      '6. Knowledge Hub — Savoirs Partagés', '6. Knowledge Hub — Shared Knowledge',
      'L\'espace de capitalisation du réseau : publiez et consultez les bonnes pratiques de co-financement, les retours d\'expérience d\'opérations réussies, les guides de structuration et les enseignements climat/ODD. Recherche, catégories, tags et votes pour faire émerger les meilleurs contenus.',
      'The network\'s knowledge base: publish and browse co-financing best practices, lessons from successful joint operations, structuring guides, and climate/SDG learnings. Search, categories, tags, and votes surface the best content.'
    ),

    // PILIER 7 — Portfolio Analytics
    s('[data-testid="nav-analytics"]',
      '7. Analytics de Portefeuille', '7. Portfolio Analytics',
      'Chiffres exacts et mis à jour en temps réel : nombre de projets, valeur du pipeline, gap de financement, deals actifs, ventilation par secteur/pays/étape, distribution du risque. Exports professionnels PDF et Excel prêts pour vos comités d\'investissement, export CSV depuis la Recherche Avancée.',
      'Exact, real-time figures: project count, pipeline value, funding gap, active deals, breakdown by sector/country/stage, risk distribution. Professional PDF and Excel exports ready for your investment committees, CSV export from Advanced Search.'
    ),

    // PILIER 8 — Governance & Security
    s('[data-testid="nav-profile"]',
      '8. Gouvernance & Sécurité Bancaire', '8. Governance & Banking-Grade Security',
      'Chaque compte ne voit que ce qu\'il doit voir : visibilité par projet (public / restreint / confidentiel), deal rooms isolées par institution, rôles et permissions (Admin, Institution, Donateur, Promoteur). Connexion par mot de passe avec authentification à deux facteurs (2FA TOTP) OBLIGATOIRE, protection anti force-brute, et piste d\'audit complète de chaque consultation, téléchargement et modification.',
      'Each account only sees what it is allowed to see: per-project visibility (public / restricted / confidential), institution-isolated deal rooms, roles and permissions (Admin, Institution, Donor, Promoter). Password sign-in with MANDATORY two-factor authentication (TOTP 2FA), brute-force protection, and a full audit trail of every view, download, and change.'
    ),

    // Secondary: quick search
    s('[data-testid="search-input"]',
      'Recherche Rapide & Avancée', 'Quick & Advanced Search',
      'Tapez un mot-clé pour filtrer instantanément. Pour des critères avancés (montant min/max, risque, étape, score de maturité) et l\'export CSV, utilisez la page "Recherche" du menu. Le Comparateur permet aussi d\'arbitrer 2-3 projets côte à côte sur 9 critères.',
      'Type a keyword to filter instantly. For advanced criteria (min/max amount, risk, stage, readiness score) and CSV export, use the "Search" page in the menu. The Comparator also lets you arbitrate 2-3 projects side by side across 9 criteria.',
      'bottom'
    ),

    // Secondary: theme + language
    s('[data-testid="lang-toggle"]',
      'Bilingue FR/EN & Thèmes', 'Bilingual FR/EN & Themes',
      'Toute l\'application bascule instantanément entre français et anglais (menus, graphiques, exports). Les synthèses, mémos et analyses IA sont générés DIRECTEMENT en français lorsque vous êtes en FR. Le bouton voisin alterne thème sombre (sessions longues) et clair (impressions). Vos préférences sont mémorisées.',
      'The entire app instantly switches between French and English (menus, charts, exports). AI summaries, memos and analyses are generated DIRECTLY in French when you are in FR. The neighboring button toggles dark (long sessions) and light (printing) themes. Your preferences are saved.',
      'bottom'
    ),

    // Secondary: logout (closing step, always rendered)
    {
      target: 'body',
      placement: 'center',
      disableBeacon: true,
      title: fr ? 'Déconnexion Sécurisée — Fin du tour' : 'Secure Logout — End of Tour',
      content: fr
        ? 'Votre session utilise un JWT chiffré de courte durée, obtenu uniquement après vérification 2FA. Déconnectez-vous systématiquement sur un poste partagé (bouton en haut à droite). Vous pouvez relancer ce tour à tout moment depuis votre Profil. Bonne exploration !'
        : 'Your session uses a short-lived encrypted JWT, issued only after 2FA verification. Always log out on a shared machine (top-right button). You can relaunch this tour anytime from your Profile. Enjoy exploring!',
    },
  ];

  if (role === 'admin') {
    steps.push(
      s('[data-testid="nav-admin"]',
        'Administration : Rôles, 2FA & Piste d\'Audit', 'Administration: Roles, 2FA & Audit Trail',
        'Réservé aux administrateurs : gestion des utilisateurs et des rôles, réinitialisation 2FA d\'un compte, et PISTE D\'AUDIT complète (consultations de projets restreints/confidentiels, téléchargements de documents, changements de visibilité, événements 2FA), exportable en CSV — la preuve de conformité exigée par les banques.',
        'Admins only: user and role management, per-account 2FA reset, and the full AUDIT TRAIL (restricted/confidential project views, document downloads, visibility changes, 2FA events), exportable to CSV — the compliance evidence banks require.'
      )
    );
  }

  return steps;
};

const OnboardingTour = () => {
  const { user } = useAuth();
  const { lang } = useLang();
  const location = useLocation();
  const [run, setRun] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [tourLang, setTourLang] = useState(lang || 'fr');
  useEffect(() => { setTourLang(lang || 'fr'); }, [lang]);
  const [steps, setSteps] = useState([]);

  // Regenerate steps when tour language changes
  useEffect(() => {
    if (!user) return;
    const role = user.role || 'institution';
    setSteps(getTourSteps(role, tourLang));
  }, [user, tourLang]);

  // Keep the tour language in sync with the app language (single source of truth)
  useEffect(() => {
    if (lang) setTourLang(lang);
  }, [lang]);

  // Show welcome on new session (only on the landing routes, never over a working page)
  useEffect(() => {
    if (!user) return;
    if (user.onboarded === false) return; // let the onboarding wizard finish first
    const path = location.pathname;
    if (path !== '/briefing' && path !== '/dashboard') return;
    const seen = sessionStorage.getItem(`${TOUR_STORAGE_KEY}_${user.user_id || user.email}`);
    if (seen) return;
    setShowWelcome(true);
  }, [user, location.pathname]);

  // Safety: never let the welcome modal block another page. If the user navigates
  // away from the landing routes while it is open, dismiss it (marks it seen).
  useEffect(() => {
    if (!showWelcome) return;
    const path = location.pathname;
    if (path !== '/briefing' && path !== '/dashboard') {
      setShowWelcome(false);
      if (user) sessionStorage.setItem(`${TOUR_STORAGE_KEY}_${user.user_id || user.email}`, 'true');
    }
  }, [location.pathname, showWelcome, user]);

  // Listen for manual relaunch from Profile page
  useEffect(() => {
    const handler = () => {
      if (!user) return;
      sessionStorage.removeItem(`${TOUR_STORAGE_KEY}_${user.user_id || user.email}`);
      setRun(false);
      setShowWelcome(true);
    };
    window.addEventListener(RELAUNCH_TOUR_EVENT, handler);
    return () => window.removeEventListener(RELAUNCH_TOUR_EVENT, handler);
  }, [user]);

  const startTour = (chosenLang) => {
    setTourLang(chosenLang);
    setShowWelcome(false);
    setTimeout(() => {
      setRun(true);
      setTimeout(() => {
        const beacon = document.querySelector('[data-testid="button-beacon"]');
        if (beacon) beacon.click();
      }, 500);
    }, 300);
  };

  const skipTour = () => {
    setShowWelcome(false);
    setRun(false);
    if (user) sessionStorage.setItem(`${TOUR_STORAGE_KEY}_${user.user_id || user.email}`, 'true');
  };

  const handleCallback = useCallback((data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRun(false);
      if (user) sessionStorage.setItem(`${TOUR_STORAGE_KEY}_${user.user_id || user.email}`, 'true');
    }
  }, [user]);

  if (!user || !steps.length) return null;
  if (!(user.nda_accepted_at && user.nda_version === user.current_nda_version)) return null;

  const isEnUI = lang === 'en';
  const isEnTour = tourLang === 'en';
  const roleLabels = {
    admin: isEnUI ? 'Administrator' : 'Administrateur',
    institution: 'Institution',
    donor: isEnUI ? 'Donor' : 'Donateur',
    project_owner: isEnUI ? 'Project Owner' : 'Promoteur',
    committee: isEnUI ? 'Investment Committee' : 'Comité d’investissement',
    secretariat: isEnUI ? 'IDFC Secretariat' : 'Secrétariat IDFC',
  };
  const stepCount = steps.length;

  // Custom tooltip with an inline FR/EN switch so users can change language without closing the tour
  const renderTooltip = ({ index, step, backProps, primaryProps, skipProps, tooltipProps, isLastStep, size }) => (
    <div {...tooltipProps} data-testid="tour-tooltip"
      style={{ backgroundColor: '#184252', color: '#CBD5E1', borderRadius: 10, border: '1px solid #285D71', padding: 20, boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', maxWidth: 420 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontSize: 11, color: '#64748B', letterSpacing: 1 }}>{index + 1} / {size}</span>
        <div style={{ display: 'flex', gap: 6 }} data-testid="tour-inline-lang">
          <button onClick={() => setTourLang('fr')} data-testid="tour-inline-fr"
            style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, border: '1px solid ' + (tourLang === 'fr' ? '#45B08F' : '#285D71'), color: tourLang === 'fr' ? '#45B08F' : '#94A3B8', background: tourLang === 'fr' ? 'rgba(69,176,143,0.1)' : 'transparent', cursor: 'pointer' }}>FR</button>
          <button onClick={() => setTourLang('en')} data-testid="tour-inline-en"
            style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 5, border: '1px solid ' + (tourLang === 'en' ? '#45B08F' : '#285D71'), color: tourLang === 'en' ? '#45B08F' : '#94A3B8', background: tourLang === 'en' ? 'rgba(69,176,143,0.1)' : 'transparent', cursor: 'pointer' }}>EN</button>
        </div>
      </div>
      {step.title && <div style={{ color: '#FFFFFF', fontSize: 16, fontWeight: 600, marginBottom: 6 }}>{step.title}</div>}
      <div style={{ color: '#94A3B8', fontSize: 13, lineHeight: 1.7 }}>{step.content}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 16 }}>
        <button {...skipProps} style={{ color: '#64748B', fontSize: 12, background: 'none', border: 'none', cursor: 'pointer' }}>
          {isEnTour ? 'Skip tour' : 'Passer le tour'}
        </button>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          {index > 0 && (
            <button {...backProps} style={{ color: '#94A3B8', fontSize: 13, background: 'none', border: 'none', cursor: 'pointer' }}>
              {isEnTour ? 'Back' : 'Retour'}
            </button>
          )}
          <button {...primaryProps} style={{ backgroundColor: '#45B08F', borderRadius: 6, color: '#FFFFFF', fontSize: 13, fontWeight: 500, padding: '8px 18px', border: 'none', cursor: 'pointer' }}>
            {isLastStep ? (isEnTour ? 'Done!' : 'Terminé !') : (isEnTour ? 'Next' : 'Suivant')}
          </button>
        </div>
      </div>
    </div>
  );

  const features = isEnUI
    ? ['1. Shared Project Pipeline', '2. Smart Partner Matching', '3. Project Intelligence (SDG Radar)', '4. Secure Collaboration (Deal Rooms)', '5. Opportunity Alerts', '6. Knowledge Hub', '7. Portfolio Analytics', '8. Governance & Security (2FA)', 'AI Analysis, OCR & Opt-out', 'AI natively in French', 'PDF/Excel/CSV Reports', 'Bilingual FR/EN']
    : ['1. Pipeline de Projets Partagé', '2. Matching Intelligent de Partenaires', '3. Intelligence Projet (Radar ODD)', '4. Collaboration Sécurisée (Deal Rooms)', '5. Alertes d\'Opportunités', '6. Knowledge Hub', '7. Analytics de Portefeuille', '8. Gouvernance & Sécurité (2FA)', 'Analyse IA, OCR & Opt-out', 'IA nativement en français', 'Rapports PDF/Excel/CSV', 'Bilingue FR/EN'];

  return (
    <>
      {showWelcome && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-[#112C38]/90 backdrop-blur-sm" data-testid="onboarding-welcome-modal">
          <div className="bg-[#184252] border border-[#285D71] rounded-lg p-6 sm:p-8 max-w-lg w-full mx-4 shadow-2xl max-h-[95vh] overflow-y-auto">
            <div className="text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#45B08F]/10 flex items-center justify-center mx-auto mb-4 sm:mb-5">
                <svg className="w-7 h-7 sm:w-8 sm:h-8 text-[#45B08F]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h2 className="text-xl sm:text-2xl font-semibold text-white mb-2" data-testid="welcome-title">
                {isEnUI ? 'Welcome to GCFE' : 'Bienvenue sur GCFE'}
              </h2>
              <p className="text-sm text-[#F5C544] font-medium mb-3 sm:mb-4">International Development Finance Club</p>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#45B08F]/10 border border-[#45B08F]/20 text-xs text-[#45B08F] mb-4 sm:mb-5">
                {isEnUI ? 'Your role' : 'Votre profil'} : {roleLabels[user.role] || 'Institution'}
              </div>
              <p className="text-sm text-slate-300 leading-relaxed mb-4">
                {isEnUI
                  ? `Discover the ${stepCount} key features of the co-financing platform for the IDFC member banks. Takes about 2 minutes.`
                  : `Découvrez les ${stepCount} fonctionnalités clés de la plateforme de co-financement des banques membres de l'IDFC. Durée : environ 2 minutes.`}
              </p>
              <div className="bg-[#112C38] border border-[#285D71] rounded p-3 mb-4">
                <p className="text-[10px] sm:text-xs text-slate-500 mb-2">{isEnUI ? 'What you\'ll discover:' : 'Ce que vous allez découvrir :'}</p>
                <div className="grid grid-cols-2 gap-1 sm:gap-1.5 text-left">
                  {features.map((item, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-[10px] sm:text-[11px] text-slate-400">
                      <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 rounded-full bg-[#45B08F] shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tour language selector */}
              <div className="bg-[#112C38] border border-[#285D71] rounded p-3 mb-5 sm:mb-6">
                <p className="text-[10px] sm:text-xs text-slate-500 mb-2 uppercase tracking-wider">
                  {isEnUI ? 'Tour language' : 'Langue du tour'}
                </p>
                <div className="flex gap-2" data-testid="tour-lang-selector">
                  <button
                    onClick={() => setTourLang('en')}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors border ${
                      tourLang === 'en'
                        ? 'bg-[#45B08F]/10 border-[#45B08F] text-[#45B08F]'
                        : 'bg-transparent border-[#285D71] text-slate-400 hover:text-white'
                    }`}
                    data-testid="tour-lang-en"
                  >
                    🇬🇧 English
                  </button>
                  <button
                    onClick={() => setTourLang('fr')}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors border ${
                      tourLang === 'fr'
                        ? 'bg-[#45B08F]/10 border-[#45B08F] text-[#45B08F]'
                        : 'bg-transparent border-[#285D71] text-slate-400 hover:text-white'
                    }`}
                    data-testid="tour-lang-fr"
                  >
                    🇫🇷 Français
                  </button>
                </div>
              </div>

              <div className="flex gap-3">
                <button onClick={skipTour} className="flex-1 px-4 py-2.5 text-sm text-slate-400 hover:text-white border border-[#285D71] rounded-lg transition-colors" data-testid="skip-tour-btn">
                  {isEnUI ? 'Skip for now' : 'Passer pour l\'instant'}
                </button>
                <button onClick={() => startTour(tourLang)} className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#45B08F] hover:bg-[#3A9679] rounded-lg transition-colors" data-testid="start-tour-btn">
                  {isEnUI ? `Start Tour (${stepCount} steps)` : `Commencer (${stepCount} étapes)`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {run && (
        <Joyride
          steps={steps}
          run={run}
          continuous
          showProgress
          scrollToFirstStep
          tooltipComponent={renderTooltip}
          onEvent={handleCallback}
          options={{ zIndex: 10000, arrowColor: '#184252', backgroundColor: '#184252', overlayColor: 'rgba(3, 12, 20, 0.85)', primaryColor: '#45B08F', textColor: '#CBD5E1' }}
          styles={{
            tooltip: { backgroundColor: '#184252', color: '#CBD5E1', borderRadius: '10px', border: '1px solid #285D71', padding: '20px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)', maxWidth: '420px' },
            tooltipTitle: { color: '#FFFFFF', fontSize: '16px', fontWeight: 600, marginBottom: '6px' },
            tooltipContent: { color: '#94A3B8', fontSize: '13px', lineHeight: '1.7' },
            buttonNext: { backgroundColor: '#45B08F', borderRadius: '6px', color: '#FFFFFF', fontSize: '13px', fontWeight: 500, padding: '8px 18px' },
            buttonBack: { color: '#94A3B8', fontSize: '13px', marginRight: '10px' },
            buttonSkip: { color: '#64748B', fontSize: '12px' },
            spotlight: { borderRadius: '8px' },
          }}
          locale={{
            back: isEnTour ? 'Back' : 'Retour',
            close: isEnTour ? 'Close' : 'Fermer',
            last: isEnTour ? 'Done!' : 'Terminé !',
            next: isEnTour ? 'Next' : 'Suivant',
            skip: isEnTour ? 'Skip tour' : 'Passer le tour',
          }}
        />
      )}
    </>
  );
};

export default OnboardingTour;

export const resetTour = (userId) => {
  sessionStorage.removeItem(`${TOUR_STORAGE_KEY}_${userId}`);
};

export const relaunchTour = () => {
  window.dispatchEvent(new CustomEvent(RELAUNCH_TOUR_EVENT));
};
